import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getWorkspace } from "../lib/workspaceStore";
import { api } from "../lib/api";
import { useCampaigns, useLibrary } from "../hooks/useQueries";
import { PageSkeleton } from "../components/PageSkeleton";

const connectionsStorageKey = "omnimark_connections_v1";

const channelPlaybooks = {
  "Meta Marketing API": {
    label: "Meta + Instagram",
    platform: "meta",
    useFor: "Sales, retargeting, lead forms, page engagement, reels/video testing",
    signals: ["CPA", "ROAS", "CTR", "creative fatigue", "winning video", "audience response"],
    bestGoal: "sales",
    avatar: "Luna Growth",
    aiRole: "Budget captain",
    action: "Start with 3-5 video creatives, watch CPA/ROAS, then duplicate winners and refresh weak hooks.",
  },
  Shopify: {
    label: "Shopify store",
    platform: "site",
    useFor: "Product feeds, order value, conversion rate, customer value, inventory context",
    signals: ["revenue", "AOV", "margin", "units sold", "refunds", "inventory risk"],
    bestGoal: "sales",
    avatar: "Zara Pipeline",
    aiRole: "Store revenue analyst",
    action: "Use product, price, image, margin, and order data to decide which offers deserve paid traffic.",
  },
  CJdropshipping: {
    label: "CJdropshipping",
    platform: "supplier",
    useFor: "Supplier costs, variants, stock, shipping, fulfillment risk",
    signals: ["cost", "shipping speed", "stock risk", "margin", "variant gaps"],
    bestGoal: "product_test",
    avatar: "Carl Demand",
    aiRole: "Supplier auditor",
    action: "Check margin and shipping before spending on ads, then flag products that can scale safely.",
  },
  "TikTok Business API": {
    label: "TikTok",
    platform: "tiktok",
    useFor: "UGC hooks, creator-style videos, trend testing, fast product validation",
    signals: ["hook rate", "watch time", "CTR", "CPC", "creative angle", "trend fit"],
    bestGoal: "traffic",
    avatar: "Cole Creative",
    aiRole: "Creative testing lead",
    action: "Launch fast hook tests, then send winners into Meta retargeting and product pages.",
  },
  "Google Ads": {
    label: "Google Ads + Analytics",
    platform: "google",
    useFor: "Search demand, high-intent traffic, conversion goals, landing-page quality",
    signals: ["search intent", "conversion rate", "CPC", "quality", "budget", "bidding"],
    bestGoal: "leads",
    avatar: "Sage Conversion",
    aiRole: "Intent strategist",
    action: "Use search intent and conversion goals when customers are already looking for the offer.",
  },
  Stripe: {
    label: "Stripe",
    platform: "revenue",
    useFor: "Revenue, refunds, customer value, subscription or purchase events",
    signals: ["revenue", "refund rate", "LTV", "payment status", "repeat purchase"],
    bestGoal: "sales",
    avatar: "Jade Conversion",
    aiRole: "Profit guard",
    action: "Compare ad spend against real revenue so OmniMark can scale profitable campaigns only.",
  },
  "Custom domain": {
    label: "Website/domain",
    platform: "site",
    useFor: "Site context, landing pages, customer chat, SEO and offer matching",
    signals: ["domain", "landing page", "offer", "FAQ", "chat questions"],
    bestGoal: "traffic",
    avatar: "Skye Brand",
    aiRole: "Landing page coach",
    action: "Match the ad promise to the landing page, FAQ, brand voice, and customer question flow.",
  },
};

const campaignTeamPresets = {
  growth: {
    label: "Growth Desk",
    note: "Best for Meta, Instagram, sales campaigns, and multi-video testing.",
    roles: ["Budget captain", "Creative testing lead", "Landing page coach", "Profit guard", "Retargeting analyst", "Audience scout"],
  },
  ecommerce: {
    label: "Ecom Scale Team",
    note: "Best for dropshipping, Shopify, supplier checks, and store revenue.",
    roles: ["Product scout", "Supplier auditor", "Store revenue analyst", "Offer builder", "Margin guard", "Fulfillment watch"],
  },
  creator: {
    label: "Creator Lab",
    note: "Best for UGC, avatar videos, hooks, scripts, and creative fatigue.",
    roles: ["Creative director", "UGC producer", "Hook analyst", "Avatar casting", "Video tester", "Comment reader"],
  },
  local: {
    label: "Lead + Local Team",
    note: "Best for local businesses, appointments, community pages, and lead forms.",
    roles: ["Intent strategist", "Inbox closer", "Community voice", "Search demand analyst", "Follow-up coach", "Trust builder"],
  },
};

function loadConnections() {
  try {
    const raw = localStorage.getItem(connectionsStorageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function connectedChannels(records) {
  const entries = Object.entries(records || {}).filter(([, record]) => (
    // Browser-local OAuth notes cannot feed live campaign metrics. A channel
    // appears here only after a server-side provider verification flow marks
    // it verified.
    record?.status === "verified"
  ));
  return entries.map(([name, record]) => ({
    name,
    record,
    playbook: channelPlaybooks[name] || {
      label: name,
      platform: "manual",
      useFor: "Manual notes, account context, and AI workflow guidance",
      signals: ["notes", "status", "account context"],
      bestGoal: "traffic",
      avatar: "OmniAssist",
      aiRole: "Setup guide",
      action: "Use saved setup notes until the official API connector is finished.",
    },
  }));
}

function recommendedPlan(channels, objective) {
  const labels = channels.map((channel) => channel.playbook.label);
  if (objective === "sales") {
    if (labels.some((x) => x.includes("Shopify")) && labels.some((x) => x.includes("Meta"))) {
      return "Use Shopify product/revenue signals to choose the offer, then run Meta + Instagram multi-video tests and retarget visitors who engaged but did not buy.";
    }
    if (labels.some((x) => x.includes("Meta"))) return "Use Meta + Instagram for multi-video sales testing, then connect store revenue next so OmniMark can judge profit instead of clicks only.";
  }
  if (objective === "product_test") {
    return "Use supplier/store data to score margin, shipping, and product demand before spending. Launch the smallest creative test first, then scale only confirmed winners.";
  }
  if (objective === "leads") {
    return "Use high-intent search or Meta lead forms, then let OmniAssist follow up with offer, FAQ, and appointment/chat context.";
  }
  return "Use the connected channels as a launch map: creative discovery first, paid traffic second, site conversion third, revenue proof fourth.";
}

function avatarInitials(name) {
  return String(name || "AI")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function avatarImage(avatar) {
  return avatar?.avatar_image_url || avatar?.image_url || avatar?.preview_url || "";
}

function buildCampaignCrew(channels, avatars, teamKey) {
  const preset = campaignTeamPresets[teamKey] || campaignTeamPresets.growth;
  const connectedMembers = (channels || []).map((channel) => ({
    id: `connected-${channel.name}`,
    name: channel.playbook.avatar,
    role: channel.playbook.aiRole,
    channel: channel.playbook.label,
    signal: channel.playbook.signals[0],
    action: channel.playbook.action,
    image: "",
    source: "Connected page",
  }));

  const selectedAvatarId = localStorage.getItem("selected_avatar_id");
  const selectedAvatarName = localStorage.getItem("selected_avatar_name");
  const selectedAvatarImage = localStorage.getItem("selected_avatar_image_url");
  const selectedAvatar = selectedAvatarId || selectedAvatarName ? [{
    id: `selected-${selectedAvatarId || selectedAvatarName}`,
    name: selectedAvatarName || "Selected Avatar",
    role: "Campaign presenter",
    channel: "Avatar Library",
    signal: "brand voice",
    action: "Represents the workspace voice in ads, scripts, and customer-facing creative.",
    image: selectedAvatarImage || "",
    source: "Selected avatar",
  }] : [];

  const libraryMembers = (avatars || []).slice(0, 8).map((avatar, index) => ({
    id: `avatar-${avatar.id || avatar.slug || index}`,
    name: avatar.name || `OmniMark Avatar ${index + 1}`,
    role: preset.roles[index % preset.roles.length],
    channel: avatar.industry_fit?.[0] || avatar.industry || "OmniMark in-house",
    signal: avatar.voice_tone || avatar.tone || avatar.personality || "creative judgment",
    action: `${avatar.name || "This avatar"} helps with ${preset.roles[index % preset.roles.length].toLowerCase()} for this campaign.`,
    image: avatarImage(avatar),
    source: "Avatar Library",
  }));

  const fallbackMembers = preset.roles.slice(0, 4).map((role, index) => ({
    id: `fallback-${teamKey}-${role}`,
    name: ["Luna Growth", "Cole Creative", "Sage Conversion", "Jade Conversion"][index] || `OmniMark ${index + 1}`,
    role,
    channel: preset.label,
    signal: ["budget", "creative", "conversion", "profit"][index] || "campaign signal",
    action: `${role} watches the campaign and recommends the next move.`,
    image: "",
    source: "OmniMark team",
  }));

  const seen = new Set();
  return [...selectedAvatar, ...connectedMembers, ...libraryMembers, ...fallbackMembers]
    .filter((member) => {
      const key = `${member.name}-${member.role}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 8);
}

function buildCoachBriefings(channels, objective, destination, videoTests, crew = []) {
  const names = channels.map((channel) => channel.playbook.label).join(", ") || "Meta + Instagram, store, supplier, or website notes";
  const team = crew.length
    ? crew.slice(0, 5).map((member) => `${member.name} handles ${member.role}`).join("; ")
    : "Luna Growth watches budget, Cole Creative watches videos, Sage Conversion watches landing-page conversion";
  return {
    before: `Before launch: confirm the offer, destination, pixel/tracking, ${videoTests} creative tests, and the ${objective.replace("_", " ")} goal. Available signals: ${names}.`,
    during: `During launch: the AI team checks spend, CTR, CPA, ROAS, video fatigue, comments, landing-page fit, and whether traffic should pause, scale, or retarget. ${team}.`,
    after: `After finish: OmniMark explains what happened, which channel carried performance, which creative won, what wasted spend, and the next action for ${destination.replace("_", " ")}.`,
  };
}

function projectedMarketSignals(channels, objective, videoTests) {
  const hasRevenue = channels.some((channel) => /Shopify|Stripe/i.test(channel.playbook.label));
  const hasMeta = channels.some((channel) => /Meta/i.test(channel.playbook.label));
  const hasSupplier = channels.some((channel) => /CJ|supplier/i.test(channel.playbook.label));
  const baseRoas = hasRevenue ? 2.6 : hasMeta ? 1.9 : 1.4;
  const risk = hasSupplier ? "Guarded" : hasRevenue ? "Balanced" : "Needs revenue proof";
  return [
    ["Projected ROAS", `${baseRoas.toFixed(1)}x`, hasRevenue ? "+ revenue feed" : "+ connect store"],
    ["Creative Index", `${Math.min(94, 64 + videoTests * 5)} / 100`, `${videoTests} video tests armed`],
    ["Spend Risk", risk, hasSupplier ? "supplier checked" : "watch margin"],
    ["AI Desk", objective === "sales" ? "Scale desk" : objective === "leads" ? "Lead desk" : "Testing desk", "active coverage"],
  ];
}

function liveMarketRows(channels, objective, crew = []) {
  const active = channels.length ? channels : [{ playbook: channelPlaybooks["Meta Marketing API"], name: "starter" }];
  const channelRows = active.flatMap((channel, index) => ([
    {
      symbol: channel.playbook.platform.toUpperCase().slice(0, 4),
      desk: channel.playbook.label,
      signal: channel.playbook.signals[index % channel.playbook.signals.length],
      move: index % 2 === 0 ? "+8.4%" : "-3.1%",
      call: index % 2 === 0 ? "Scale watch" : "Refresh watch",
    },
    {
      symbol: channel.playbook.avatar.split(" ")[0].toUpperCase().slice(0, 4),
      desk: channel.playbook.aiRole,
      signal: objective === "sales" ? "profit pressure" : "response quality",
      move: index % 2 === 0 ? "+12.0%" : "+4.7%",
      call: channel.playbook.action,
    },
  ]));
  const crewRows = (crew || []).slice(0, 4).map((member, index) => ({
    symbol: avatarInitials(member.name),
    desk: member.role,
    signal: member.signal,
    move: index % 2 === 0 ? "+9.8%" : "+5.2%",
    call: member.action,
  }));
  return [...crewRows, ...channelRows].slice(0, 8);
}

function cleanTitle(value, fallback = "Workspace ad pack") {
  if (typeof value !== "string") return fallback;
  const cleaned = value.trim().replace(/\s+/g, " ");
  return cleaned || fallback;
}

function normalizeObjective(value, fallback) {
  const text = String(value || "").toLowerCase();
  if (text.includes("lead")) return "leads";
  if (text.includes("traffic") || text.includes("visit")) return "traffic";
  if (text.includes("product") || text.includes("test")) return "product_test";
  if (text.includes("sale") || text.includes("revenue") || text.includes("conversion")) return "sales";
  return fallback;
}

function generationText(generation) {
  try {
    return JSON.stringify({
      input: generation?.input_json || {},
      output: generation?.output_json || {},
      type: generation?.type,
    }).toLowerCase();
  } catch {
    return String(generation?.type || "").toLowerCase();
  }
}

function buildWorkspaceAdIdeas(generations, objective) {
  return (generations || [])
    .filter((generation) => {
      const text = generationText(generation);
      return /meta_ad|ad|creative|campaign|hook|headline|product|service|offer|studio/i.test(`${generation?.type || ""} ${text}`);
    })
    .slice(0, 8)
    .map((generation, index) => {
      const input = generation.input_json || {};
      const request = input.request || input;
      const output = generation.output_json || {};
      const product = cleanTitle(
        request.product_or_service
          || input.product_or_service
          || output.product_or_service
          || output.offer
          || output.product
          || output.headline
          || output.title
          || generation.type,
        "Workspace ad pack"
      );
      const hook = cleanTitle(
        output.hook
          || output.headline
          || output.primary_text
          || output.cole_creative?.headline
          || output.cole_creative?.hook
          || output.results?.cole_creative?.headline
          || output.results?.cole_creative?.hook,
        ""
      );
      const goal = normalizeObjective(request.goal || input.goal || output.goal || output.objective, objective);
      const platform = request.platform || input.platform || "meta";
      return {
        label: `Workspace ad: ${product}${hook ? ` - ${hook}` : ""}`,
        name: `${product} ${goal === "product_test" ? "validation" : goal} campaign`,
        platform,
        objective: goal,
        source: "Workspace ad creation",
        context: {
          generation_id: generation.id,
          generation_type: generation.type,
          product_or_service: product,
          creative_hook: hook,
          created_at: generation.created_at,
        },
        rank: index,
      };
    });
}

function buildCampaignSuggestions(channels, objective, destination, items, generations) {
  const connected = channels.length ? channels : [{ playbook: channelPlaybooks["Meta Marketing API"], name: "starter" }];
  const workspaceAdIdeas = buildWorkspaceAdIdeas(generations, objective);
  const existing = (items || []).slice(0, 4).map((item) => ({
    label: `Reuse: ${item.name}`,
    name: `${item.name} refresh`,
    platform: item.platform || "meta",
    objective,
    source: "Existing campaign",
  }));
  const channelIdeas = connected.flatMap((channel) => {
    const label = channel.playbook.label;
    if (/Meta/i.test(label)) {
      return [
        { label: "Meta sales sprint", name: "Meta + Instagram sales sprint", platform: "meta", objective: "sales", source: "Connected Meta account" },
        { label: "Retarget warm audience", name: "Warm audience retargeting push", platform: "meta", objective: "sales", source: "Connected Meta account" },
        { label: "Lead form test", name: "Meta lead form conversion test", platform: "meta", objective: "leads", source: "Connected Meta account" },
      ];
    }
    if (/TikTok/i.test(label)) {
      return [
        { label: "UGC hook test", name: "TikTok UGC hook test", platform: "tiktok", objective: "traffic", source: "Connected TikTok account" },
        { label: "Product discovery burst", name: "TikTok product discovery burst", platform: "tiktok", objective: "product_test", source: "Connected TikTok account" },
      ];
    }
    if (/Google/i.test(label)) {
      return [
        { label: "High-intent search", name: "Google high-intent search campaign", platform: "google", objective: "leads", source: "Connected Google account" },
        { label: "Conversion recovery", name: "Google conversion recovery test", platform: "google", objective: "sales", source: "Connected Google account" },
      ];
    }
    if (/Shopify|Stripe/i.test(label)) {
      return [
        { label: "Best seller scale", name: "Best seller scale campaign", platform: "meta", objective: "sales", source: "Store/revenue signal" },
        { label: "Profit guard test", name: "Profit guarded sales test", platform: "meta", objective: "sales", source: "Store/revenue signal" },
      ];
    }
    if (/CJ|supplier/i.test(label)) {
      return [
        { label: "Winning product test", name: "Winning product validation test", platform: "supplier", objective: "product_test", source: "Supplier signal" },
      ];
    }
    return [
      { label: `${label} campaign`, name: `${label} guided campaign`, platform: channel.playbook.platform, objective, source: "Connected account" },
    ];
  });
  const destinationIdea = destination === "omnimark_site"
    ? [{ label: "OmniMark hosted page", name: "OmniMark hosted landing page campaign", platform: "site", objective, source: "Destination choice" }]
    : [];
  return [...workspaceAdIdeas, ...destinationIdea, ...channelIdeas, ...existing].slice(0, 16);
}

export default function CampaignsPage() {
  const ws = getWorkspace();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useCampaigns(ws?.id);
  const { data: libraryData } = useLibrary(ws?.id);

  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("meta");
  const [objective, setObjective] = useState("sales");
  const [destination, setDestination] = useState("customer_site");
  const [campaignSource, setCampaignSource] = useState("manual");
  const [campaignLookupContext, setCampaignLookupContext] = useState(null);
  const [activeTeamKey, setActiveTeamKey] = useState("growth");
  const [avatarOptions, setAvatarOptions] = useState([]);
  const [createErr, setCreateErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notifyStatus, setNotifyStatus] = useState(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission
  );
  const [videoTests, setVideoTests] = useState(3);
  const [records] = useState(loadConnections);
  useEffect(() => {
    let mounted = true;
    async function loadCampaignAvatars() {
      try {
        const res = await api.get("/api/avatars?limit=18&offset=0");
        if (mounted) setAvatarOptions(res.data.items || []);
      } catch {
        if (mounted) setAvatarOptions([]);
      }
    }
    loadCampaignAvatars();
    return () => {
      mounted = false;
    };
  }, []);

  const items = useMemo(() => data?.items || [], [data?.items]);
  const generations = useMemo(() => libraryData?.items || [], [libraryData?.items]);
  const err = error?.response?.data?.detail || (error ? "Failed to load campaigns." : null) || createErr;
  const channels = useMemo(() => connectedChannels(records), [records]);
  const connectedLabels = channels.map((channel) => channel.playbook.label);
  const campaignCrew = useMemo(() => buildCampaignCrew(channels, avatarOptions, activeTeamKey), [activeTeamKey, avatarOptions, channels]);
  const plan = recommendedPlan(channels, objective);
  const coachBriefings = useMemo(() => buildCoachBriefings(channels, objective, destination, videoTests, campaignCrew), [campaignCrew, channels, destination, objective, videoTests]);
  const marketSignals = useMemo(() => projectedMarketSignals(channels, objective, videoTests), [channels, objective, videoTests]);
  const marketRows = useMemo(() => liveMarketRows(channels, objective, campaignCrew), [campaignCrew, channels, objective]);
  const campaignSuggestions = useMemo(
    () => buildCampaignSuggestions(channels, objective, destination, items, generations),
    [channels, destination, generations, items, objective]
  );
  const recommendedPlatform = channels.find((channel) => channel.playbook.bestGoal === objective)?.playbook.platform
    || channels[0]?.playbook.platform
    || platform;

  async function create() {
    setCreateErr(null);
    setBusy(true);
    try {
      await api.post(`/api/workspaces/${ws.id}/campaigns`, {
        name,
        platform,
        input: {
          objective,
          campaign_goal: objective,
          destination,
          video_tests: videoTests,
          optimization_mode: "multi_video_realtime_ai",
          notifications_opt_in: notifyStatus === "granted",
          connected_channels: connectedLabels,
          ai_campaign_team: campaignCrew.map((member) => ({
            avatar: member.name,
            role: member.role,
            channel: member.channel,
            signal: member.signal,
            source: member.source,
            action: member.action,
          })),
          ai_team_preset: campaignTeamPresets[activeTeamKey]?.label || "Growth Desk",
          ai_recommendation: plan,
          ai_coach_briefings: coachBriefings,
          projected_market_signals: marketSignals,
          campaign_source: campaignSource,
          campaign_lookup_context: campaignLookupContext,
        },
        metrics: {},
      });
      setName("");
      setCampaignLookupContext(null);
      queryClient.invalidateQueries({ queryKey: ["campaigns", ws.id] });
    } catch (e) {
      setCreateErr(e?.response?.data?.detail || "Failed to create campaign.");
    } finally {
      setBusy(false);
    }
  }

  if (!ws) {
    return (
      <div style={{ padding: 16, color: "#666" }}>
        Please select a workspace first.
      </div>
    );
  }

  if (isLoading) return <PageSkeleton />;

  async function enableNotifications() {
    if (typeof Notification === "undefined") {
      setNotifyStatus("unsupported");
      return;
    }
    const permission = await Notification.requestPermission();
    setNotifyStatus(permission);
    if (permission === "granted") {
      new Notification("OmniMark HQ notifications enabled", {
        body: "AI campaign alerts can tell you when ads need new hooks, videos, budgets, or pauses.",
      });
    }
  }

  function applyCampaignSuggestion(value) {
    if (!value) return;
    const suggestion = campaignSuggestions.find((item) => item.name === value || item.label === value);
    if (!suggestion) return;
    setName(suggestion.name);
    setPlatform(suggestion.platform || "meta");
    setObjective(suggestion.objective || objective);
    setCampaignSource(suggestion.source || "AI suggestion");
    setCampaignLookupContext(suggestion.context || null);
  }

  return (
    <div style={page} data-testid="campaigns-page">
      <section style={hero}>
        <div>
          <div style={eyebrow}>AI launch command center</div>
          <h1 style={title}>Campaigns</h1>
          <p style={subtitle}>
            OmniMark reads your connected accounts, assigns the right AI avatar team, and recommends which channel should drive sales, leads, traffic, or product testing.
          </p>
        </div>
        <Link to="/app/connections" style={heroButton}>
          Manage connections
        </Link>
      </section>

      {err && <div style={{ marginTop: 10, color: "#b00020" }}>{err}</div>}

      <div style={livePanel}>
        <div>
          <div style={{ fontWeight: 900 }}>Real-time AI campaign team</div>
          <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
            OmniMark tracks creative fatigue, winning videos, budget waste, audience response, revenue quality, and when a customer should pause, scale, refresh, or retarget.
          </div>
        </div>
        <button onClick={enableNotifications} style={notifyStatus === "granted" ? btnGood : btnOutline}>
          {notifyStatus === "granted" ? "Notifications on" : "Enable alerts"}
        </button>
      </div>

      <div style={signalGrid}>
        {[
          ["Video testing", `${videoTests} creatives per campaign`],
          ["AI alerts", notifyStatus === "granted" ? "Opted in" : "Needs opt-in"],
          ["Connected channels", connectedLabels.length ? connectedLabels.join(", ") : "Connect Meta, store, or supplier"],
          ["Recommended lane", recommendedPlatform.toUpperCase()],
        ].map(([k, v]) => (
          <div key={k} style={signalCard}>
            <div style={{ fontWeight: 900 }}>{k}</div>
            <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>{v}</div>
          </div>
        ))}
      </div>

      <section style={coachGrid}>
        <div style={marketPanel}>
          <div style={marketHeader}>
            <div>
              <div style={eyebrowDark}>Campaign market desk</div>
              <div style={sectionTitle}>Projected, Live, and After-Campaign Analytics</div>
            </div>
            <span style={liveBadge}>{connectedLabels.length ? "AI team active" : "Planning mode"}</span>
          </div>
          <div style={tickerGrid}>
            {marketSignals.map(([label, value, note]) => (
              <div key={label} style={tickerCard}>
                <span>{label}</span>
                <strong>{value}</strong>
                <small>{note}</small>
              </div>
            ))}
          </div>
          <div style={marketAvatarStrip}>
            {campaignCrew.slice(0, 5).map((member) => (
              <div key={`desk-${member.id}`} style={marketAvatarChip}>
                {member.image ? (
                  <img src={member.image} alt="" style={marketAvatarImage} />
                ) : (
                  <span style={marketAvatarInitial}>{avatarInitials(member.name)}</span>
                )}
                <span>{member.role}</span>
              </div>
            ))}
          </div>
          <div style={marketTable}>
            <div style={marketRowHead}>
              <span>Desk</span>
              <span>Signal</span>
              <span>Move</span>
              <span>AI Call</span>
            </div>
            {marketRows.map((row) => (
              <div key={`${row.symbol}-${row.signal}-${row.desk}`} style={marketRow}>
                <strong>{row.symbol}</strong>
                <span>{row.signal}</span>
                <span style={row.move.startsWith("+") ? upMove : downMove}>{row.move}</span>
                <small>{row.call}</small>
              </div>
            ))}
          </div>
        </div>

        <div style={coachPanel}>
          <div style={eyebrowDark}>AI coach briefings</div>
          {[
            ["Before launch", coachBriefings.before, "Plan, tracking, offer, creative tests"],
            ["During campaign", coachBriefings.during, "Live watch, alerts, pause/scale calls"],
            ["After finish", coachBriefings.after, "Plain-English results and next move"],
          ].map(([phase, copy, note]) => (
            <div key={phase} style={coachCard}>
              <div style={phaseTop}>
                <strong>{phase}</strong>
                <span>{note}</span>
              </div>
              <p>{copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={commandGrid}>
        <div style={panel}>
          <div style={sectionTitle}>Connected Channel Intelligence</div>
          <p style={muted}>
            These are the accounts OmniMark can use right now. When a channel is connected, it becomes part of the campaign brain instead of sitting on the Connections page.
          </p>
          <div style={channelGrid}>
            {channels.length === 0 ? (
              <div style={emptyState}>
                No connected social, store, or supplier account yet. Connect Meta + Instagram, Shopify, TikTok, Google, Stripe, or supplier feeds so campaigns can use live context.
                <Link to="/app/connections" style={inlineLink}>Open Connections</Link>
              </div>
            ) : (
              channels.map((channel) => (
                <div key={channel.name} style={channelCard}>
                  <div style={channelTop}>
                    <strong>{channel.playbook.label}</strong>
                    <span style={readyPill}>Connected</span>
                  </div>
                  <div style={mutedSmall}>{channel.playbook.useFor}</div>
                  <div style={chipRow}>
                    {channel.playbook.signals.slice(0, 5).map((signal) => <span key={signal} style={chip}>{signal}</span>)}
                  </div>
                  <div style={adviceBox}>{channel.playbook.action}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={panel}>
          <div style={sectionTitle}>Avatar Team Assignment</div>
          <p style={muted}>Choose the AI crew for this campaign. OmniMark blends connected pages with in-house avatars from your Avatar Library.</p>
          <div style={teamSelector}>
            {Object.entries(campaignTeamPresets).map(([key, preset]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTeamKey(key)}
                style={activeTeamKey === key ? teamButtonActive : teamButton}
              >
                <strong>{preset.label}</strong>
                <span>{preset.note}</span>
              </button>
            ))}
          </div>
          <div style={avatarGrid}>
            {campaignCrew.slice(0, 6).map((member) => (
              <div key={member.id} style={avatarCard}>
                {member.image ? (
                  <img src={member.image} alt="" style={avatarPhoto} />
                ) : (
                  <div style={avatarCircle}>{avatarInitials(member.name)}</div>
                )}
                <div>
                  <strong>{member.name}</strong>
                  <div style={mutedSmall}>{member.role}</div>
                  <div style={tinyText}>{member.channel} • {member.source}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={avatarLibraryLine}>
            {avatarOptions.length ? `${avatarOptions.length} in-house avatars available for campaigns.` : "Connect Avatar Library to bring generated in-house avatars into the team."}
            <Link to="/app/avatars" style={inlineLink}>Open Avatar Library</Link>
          </div>
          <div style={strategyBox}>
            <strong>OmniMark recommendation</strong>
            <span>{plan}</span>
          </div>
        </div>
      </section>

      <div style={createGrid}>
        <div style={nameBuilder}>
          <label style={miniLabel}>Campaign name or AI lookup</label>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setCampaignSource("manual");
              setCampaignLookupContext(null);
            }}
            placeholder="Search workspace ads, ad packs, campaigns, or type a name"
            list="campaign-name-suggestions"
            style={inputStyle}
          />
          <datalist id="campaign-name-suggestions">
            {campaignSuggestions.map((suggestion) => (
              <option key={`${suggestion.source}-${suggestion.name}`} value={suggestion.name}>
                {suggestion.label}
              </option>
            ))}
          </datalist>
          <select
            value=""
            onChange={(e) => applyCampaignSuggestion(e.target.value)}
            style={inputStyle}
            aria-label="Choose campaign suggestion"
          >
            <option value="">Search workspace ads and campaign ideas</option>
            {campaignSuggestions.map((suggestion) => (
              <option key={`${suggestion.label}-${suggestion.name}`} value={suggestion.name}>
                {suggestion.label} - {suggestion.source}
              </option>
            ))}
          </select>
          <div style={sourceHint}>
            {campaignSource === "manual"
              ? "Manual name. OmniMark will still use connected accounts and AI coach briefings."
              : `Guided by ${campaignSource}.`}
          </div>
        </div>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={inputStyle}>
          <option value="meta">Meta + Instagram</option>
          <option value="tiktok">TikTok</option>
          <option value="google">Google</option>
          <option value="site">Website/site</option>
          <option value="supplier">Supplier/product test</option>
        </select>
        <select value={objective} onChange={(e) => setObjective(e.target.value)} style={inputStyle}>
          <option value="sales">Sales</option>
          <option value="leads">Leads</option>
          <option value="traffic">Traffic</option>
          <option value="product_test">Product test</option>
          <option value="awareness">Awareness</option>
        </select>
        <select value={destination} onChange={(e) => setDestination(e.target.value)} style={inputStyle}>
          <option value="customer_site">Customer site</option>
          <option value="omnimark_site">OmniMark hosted page</option>
          <option value="marketplace">Marketplace/storefront</option>
          <option value="lead_form">Lead form/chat</option>
        </select>
        <select value={videoTests} onChange={(e) => setVideoTests(Number(e.target.value))} style={inputStyle}>
          <option value={2}>2 videos</option>
          <option value={3}>3 videos</option>
          <option value={5}>5 videos</option>
          <option value={8}>8 videos</option>
        </select>
        <button onClick={create} disabled={busy || !name.trim()} style={btn}>
          {busy ? "Creating…" : "Create"}
        </button>
      </div>

      <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
        {items.length === 0 ? (
          <div style={{ color: "#999", padding: 20, textAlign: "center" }}>
            No campaigns yet. Create one above.
          </div>
        ) : (
          items.map((c) => (
            <Link key={c.id} to={`/app/campaigns/${c.id}`} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 900 }}>{c.name}</div>
                <div style={miniPill}>AI monitoring</div>
              </div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                {c.platform.toUpperCase()} • {c.status} • multi-video testing
              </div>
              <div style={campaignBriefGrid}>
                <span>Before: offer and tracking checked</span>
                <span>During: AI desk watches signals</span>
                <span>After: coach debrief ready</span>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.12)",
  padding: "10px 12px",
  outline: "none",
  background: "#fff",
  fontFamily: "inherit",
  fontSize: 14,
};

const btn = {
  borderRadius: 12,
  border: "none",
  padding: "10px 12px",
  fontWeight: 900,
  background: "#111",
  color: "#fff",
  cursor: "pointer",
  fontSize: 14,
};

const btnOutline = {
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.12)",
  padding: "10px 12px",
  fontWeight: 900,
  background: "#fff",
  color: "#111",
  cursor: "pointer",
  fontSize: 14,
};

const btnGood = {
  ...btnOutline,
  borderColor: "#34d399",
  background: "#ecfdf5",
  color: "#065f46",
};

const livePanel = {
  marginTop: 14,
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.10)",
  padding: 14,
  background: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};

const signalGrid = {
  marginTop: 10,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
  gap: 10,
};

const signalCard = {
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.10)",
  padding: 12,
  background: "#f8fafc",
};

const miniPill = {
  borderRadius: 999,
  background: "#ecfeff",
  color: "#155e75",
  padding: "4px 8px",
  fontSize: 12,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const card = {
  display: "block",
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.10)",
  padding: 12,
  background: "#fff",
  textDecoration: "none",
  color: "#111",
};

const page = { padding: 16, maxWidth: 1160, margin: "0 auto", display: "grid", gap: 14 };
const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: 16,
  borderRadius: 8,
  padding: 22,
  background: "linear-gradient(135deg,#07121f,#12392f 58%,#3b2f75)",
  color: "#fff",
  flexWrap: "wrap",
};
const eyebrow = { color: "#9ee7d4", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0 };
const eyebrowDark = { color: "#0f766e", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0 };
const title = { margin: "6px 0", fontSize: 34, lineHeight: 1.05 };
const subtitle = { margin: 0, color: "rgba(255,255,255,.78)", maxWidth: 760, lineHeight: 1.45 };
const heroButton = { borderRadius: 8, background: "#fff", color: "#101828", padding: "10px 12px", fontWeight: 900, textDecoration: "none", whiteSpace: "nowrap" };
const commandGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 };
const panel = { borderRadius: 8, border: "1px solid rgba(0,0,0,.10)", background: "#fff", padding: 14, minWidth: 0 };
const sectionTitle = { fontWeight: 900, fontSize: 18 };
const muted = { color: "#667085", margin: "6px 0 12px", fontSize: 13, lineHeight: 1.45 };
const mutedSmall = { color: "#667085", fontSize: 13, lineHeight: 1.4 };
const tinyText = { color: "#475467", fontSize: 12, marginTop: 2 };
const channelGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 };
const channelCard = { border: "1px solid rgba(0,0,0,.10)", borderRadius: 8, background: "#fbfcff", padding: 12, display: "grid", gap: 9 };
const channelTop = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 };
const readyPill = { borderRadius: 999, background: "#dcfce7", color: "#166534", padding: "3px 8px", fontSize: 12, fontWeight: 900, whiteSpace: "nowrap" };
const chipRow = { display: "flex", flexWrap: "wrap", gap: 6 };
const chip = { borderRadius: 999, background: "#eef6ff", color: "#1e3a8a", padding: "3px 8px", fontSize: 12, fontWeight: 800 };
const adviceBox = { borderRadius: 8, background: "#f8fafc", border: "1px solid #e5e7eb", padding: 10, color: "#344054", fontSize: 13, lineHeight: 1.4 };
const emptyState = { display: "grid", gap: 10, border: "1px dashed #cbd5e1", borderRadius: 8, padding: 14, color: "#475467", background: "#f8fafc", fontSize: 13, lineHeight: 1.45 };
const inlineLink = { color: "#111827", fontWeight: 900 };
const avatarGrid = { display: "grid", gap: 10 };
const avatarCard = { display: "grid", gridTemplateColumns: "44px 1fr", gap: 10, alignItems: "center", border: "1px solid rgba(0,0,0,.08)", borderRadius: 8, padding: 10, background: "#fbfcff" };
const avatarCircle = { width: 44, height: 44, borderRadius: 999, display: "grid", placeItems: "center", background: "#111827", color: "#fff", fontWeight: 900 };
const avatarPhoto = { width: 44, height: 44, borderRadius: 999, objectFit: "cover", background: "#eef2f7", border: "1px solid rgba(0,0,0,.08)" };
const teamSelector = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(148px,1fr))", gap: 8, marginBottom: 10 };
const teamButton = { borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", padding: 10, textAlign: "left", display: "grid", gap: 4, cursor: "pointer", color: "#111827", fontFamily: "inherit" };
const teamButtonActive = { ...teamButton, borderColor: "#111827", background: "#111827", color: "#fff" };
const avatarLibraryLine = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 10, color: "#667085", fontSize: 12, lineHeight: 1.35 };
const strategyBox = { marginTop: 12, display: "grid", gap: 5, borderRadius: 8, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", padding: 12, fontSize: 13, lineHeight: 1.45 };
const createGrid = { marginTop: 2, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 };
const nameBuilder = { display: "grid", gap: 7, gridColumn: "span 2", minWidth: 0 };
const miniLabel = { color: "#344054", fontSize: 12, fontWeight: 900 };
const sourceHint = { color: "#667085", fontSize: 12, lineHeight: 1.35 };
const coachGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 };
const marketPanel = { borderRadius: 8, border: "1px solid rgba(15,23,42,.14)", background: "#0f172a", color: "#fff", padding: 14, display: "grid", gap: 12, minWidth: 0 };
const marketHeader = { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 10, flexWrap: "wrap" };
const liveBadge = { borderRadius: 999, background: "#dcfce7", color: "#166534", padding: "5px 9px", fontSize: 12, fontWeight: 900, whiteSpace: "nowrap" };
const tickerGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))", gap: 8 };
const tickerCard = { borderRadius: 8, border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.06)", padding: 10, display: "grid", gap: 3 };
const marketAvatarStrip = { display: "flex", flexWrap: "wrap", gap: 8 };
const marketAvatarChip = { display: "inline-flex", alignItems: "center", gap: 7, borderRadius: 999, border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.07)", padding: "5px 9px", color: "#e5e7eb", fontSize: 12, fontWeight: 800 };
const marketAvatarImage = { width: 24, height: 24, borderRadius: 999, objectFit: "cover", background: "#fff" };
const marketAvatarInitial = { width: 24, height: 24, borderRadius: 999, display: "grid", placeItems: "center", background: "#e0f2fe", color: "#075985", fontSize: 10, fontWeight: 900 };
const marketTable = { display: "grid", gap: 5, overflowX: "auto" };
const marketRowHead = { display: "grid", gridTemplateColumns: "70px minmax(110px,1fr) 70px minmax(160px,1.4fr)", gap: 8, color: "#cbd5e1", fontSize: 12, padding: "0 4px" };
const marketRow = { display: "grid", gridTemplateColumns: "70px minmax(110px,1fr) 70px minmax(160px,1.4fr)", gap: 8, alignItems: "center", borderRadius: 8, background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.08)", padding: "9px 8px", minWidth: 560 };
const upMove = { color: "#86efac", fontWeight: 900 };
const downMove = { color: "#fca5a5", fontWeight: 900 };
const coachPanel = { borderRadius: 8, border: "1px solid rgba(0,0,0,.10)", background: "#fff", padding: 14, display: "grid", gap: 10 };
const coachCard = { borderRadius: 8, border: "1px solid #e5e7eb", background: "#fbfcff", padding: 12, display: "grid", gap: 6 };
const phaseTop = { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", color: "#111827" };
const campaignBriefGrid = { marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, color: "#344054", fontSize: 12 };
